/**
 * Englische Texte der Einstellungsseite.
 *
 * Aufbau und Reihenfolge folgen roboter-einstellungen.ts; Aufrufe, Zeitpunkte
 * und Quellen kommen von dort. roboter-einstellungen-sprachen.test.ts prueft,
 * dass beides zusammenpasst.
 */

import type { Texte } from "./roboter-einstellungen-sprachen";

const en: Texte = {
  zeitpunkt: {
    jederzeit: "at any time, even while driving",
    stillstand: "only while stationary – otherwise RobotBusyError",
    "vor-dem-start": "once, at the start of the program",
    blockiert: "blocks the program until it has finished",
  },
  grundsaetze: [
    "Settings apply from the call until the end of the program. After every start – also after sending and after switching on – the defaults apply again. That is why they belong in the program, right after robot.init().",
    "If a value is left out in the call, it stays unchanged. drive.configure(max_speed_m_s=0.6) changes only the maximum speed.",
    "All values are SI units: metres, seconds, radians. The ending of the name tells you: _m_s is m/s, _rad_s is rad/s, _s is seconds.",
    "“Only while stationary” means: as long as a drive or motor command is using the drive, the call answers with RobotBusyError. Call robot.stop() or end the command first.",
    "Invalid values are not silently adjusted but rejected with ValueError. The error message names the permitted range.",
  ],
  gruppen: {
    start: {
      titel: "Start and pin assignment",
      einleitung:
        "robot.init() checks the wiring, starts the hardware and measures the battery. Without this call all other settings have no effect. The motors stay off.",
      einstellungen: [
        {
          name: "Pin assignment",
          vorgabe: "none – it is required",
          bereich:
            "GPIO 0–22 and 26–28. battery_adc only 26, 27 or 28 (analog inputs). imu.int may be left out; the firmware does not use the pin, it is only recorded as in use. Each pin at most once.",
          wirkung:
            "Defines which connections of the Pico the motors, encoders, orientation sensor, ultrasonic sensor, buttons, LED and battery measurement are attached to.",
          hinweis:
            "GPIO 23, 24, 25 and 29 belong to the radio module and the board's power supply and are blocked. A missing, duplicate or invalid entry aborts with PinError – and the message names the place.",
        },
      ],
      fest: [
        {
          name: "Battery measurement at start-up",
          wert: "25 measurements, 2 ms apart",
          bedeutung:
            "robot.init() blocks for about 50 ms for this. If the measurement fails, init aborts with RobotFaultError – without a valid voltage the motors stay locked.",
        },
      ],
    },
    fahrwerk: {
      titel: "Drive base",
      einleitung:
        "Limits and behaviour for drive.velocity(), drive.straight(), drive.turn() and all other drive commands. If a command exceeds a limit, the robot rejects it with ValueError – it does not simply drive more slowly.",
      einstellungen: [
        {
          name: "Maximum speed",
          vorgabe: "0.4 m/s",
          bereich: "greater than 0",
          wirkung:
            "Drive commands with a higher speed are rejected: drive.velocity(0.5, 0) raises ValueError and ends the program unless it catches the error.",
          hinweis:
            "The 0.4 m/s is a placeholder and was never determined on the robot. It is meant to stop a typo such as drive.velocity(4, 0) from making the robot shoot off. Watch out with the joystick: y * 0.5 gives 0.5 m/s at full deflection, which is above it.",
        },
        {
          name: "Maximum turn rate",
          vorgabe: "3.0 rad/s (about 172 °/s)",
          bereich: "greater than 0",
          wirkung: "Turn commands above this rate are rejected with ValueError.",
          hinweis: "Like the maximum speed, a placeholder that was never determined.",
        },
        {
          name: "Acceleration",
          vorgabe: "no ramp – the setpoint jumps",
          bereich: "greater than 0",
          wirkung:
            "Limits how fast the desired speed may change. The robot starts and brakes more smoothly.",
          hinweis:
            "Without a ramp the robot jerks when starting, and a balancing build can tip over.",
        },
        {
          name: "Behaviour at the end of a drive job",
          vorgabe: '"coast" – coast to a stop',
          bereich: '"coast" coast, "brake" brake, "hold" hold position',
          wirkung:
            "What the motors do when drive.straight() or drive.turn() have reached their target.",
          hinweis:
            "\"hold\" keeps controlling and uses power doing so. For the all-round stop there is robot.stop(), not this setting.",
        },
        {
          name: "Time limit for drive jobs",
          vorgabe: "20 s",
          bereich: "greater than 0",
          wirkung:
            "A job with a target – distance, angle, time – that does not reach its target within this time is aborted and reports this.",
          hinweis:
            "Does not apply to continuous commands such as drive.velocity() or motor_left.run(): they run until another command arrives.",
        },
      ],
      fest: [
        {
          name: "Wheel diameter",
          wert: "90 mm",
          bedeutung:
            "Converts wheel revolutions into metres. Provisional value, still to be confirmed on the robot – if it is wrong, distances and speeds are wrong.",
        },
        {
          name: "Track width",
          wert: "110 mm",
          bedeutung:
            "Distance between the wheels; determines how far the robot turns in a rotation. Provisional value, still to be confirmed on the robot.",
        },
      ],
    },
    motoren: {
      titel: "Motors and speed controllers",
      einleitung:
        "Each motor has two controllers: one for the speed (motor_left.run(), all drive commands) and one for the angle (motor_left.run_to_angle(), motor_left.run_for_angle()). Settings apply per motor – so call them twice for both sides.",
      einstellungen: [
        {
          name: "Speed controller",
          vorgabe: "kp 6.0 · ki 12.0 · kd 0.0",
          bereich: 'loop "speed" or "position"; gains any numbers',
          wirkung:
            "How strongly the controller reacts to a speed error. kp immediately, ki over time, kd to the change.",
          hinweis:
            "The default is a placeholder; the controllers are not tuned. On the robot, kp = 8 and ki = 40 have worked well. Changing them while driving is explicitly allowed – the accumulated I term is kept so that the speed does not jump. Works well with a slider from remote.number().",
        },
        {
          name: "Angle controller",
          vorgabe: "kp 4.0 · ki 0.0 · kd 0.2",
          wirkung: "How the motor approaches and holds a target angle.",
        },
        {
          name: "Maximum duty cycle",
          vorgabe: "100 %",
          bereich: "above 0 up to 100",
          wirkung: "Caps the motor drive in percent.",
          hinweis:
            "Independently of this, the 6 V limit always applies (see Safety): with a full battery, 100 % here is never 100 % at the motor.",
        },
        {
          name: "Acceleration",
          vorgabe: "no ramp",
          bereich: "greater than 0",
          wirkung: "Limits how fast the desired speed may change, in rad/s².",
        },
        {
          name: "Behaviour at the end of a job",
          vorgabe: '"coast" – coast to a stop',
          bereich: '"coast", "brake", "hold"',
          wirkung: "What the motor does when a job with a target has finished.",
        },
        {
          name: "Time limit for jobs",
          vorgabe: "20 s",
          bereich: "greater than 0",
          wirkung: "Aborts a job with a target that does not reach it within this time.",
          hinweis: "Does not apply to motor_left.run() – a continuous command has no target.",
        },
        {
          name: "Stall detection",
          vorgabe: "0.5 s",
          bereich: "greater than 0",
          wirkung:
            "If the wheel stands still this long although the controller requests at least 25 % duty cycle, the drive counts as blocked: the controller switches off, the job ends, and the motor stops according to its stop_mode.",
          hinweis:
            "The first 1.5 s after starting are not checked – otherwise starting against static friction looks exactly like a stall. With very weak controller gains a motor can wrongly count as blocked.",
        },
      ],
      fest: [
        {
          name: "PWM frequency",
          wert: "500 Hz",
          bedeutung: "Frequency at which the motors are driven.",
        },
      ],
    },
    regler: {
      titel: "Your own controller",
      einleitung:
        "control.start() runs a function of your own on the control core, at a fixed rate and independently of the program. control.PID is a ready-made controller with limits and anti-windup.",
      einstellungen: [
        {
          name: "Control interval",
          vorgabe: "none – it is required",
          bereich: "0.002 s to 1.0 s",
          wirkung: "How often your own function is called.",
          hinweis:
            "Faster than the base cycle of 2 ms is not possible. control.timing() shows whether the rate is kept – look there first if a controller behaves strangely.",
        },
        {
          name: "Permitted run time per call",
          vorgabe: "half the period",
          bereich: "above 0 up to period_s",
          wirkung: "If a call takes longer, it is counted as a missed deadline.",
        },
        {
          name: "PID controller",
          vorgabe: "none – gains when creating it",
          bereich: "limits as (lower, upper); reverse=True reverses the sign",
          wirkung:
            "Calculates an output value with pid.update(soll, ist, dt_s). pid.kp, pid.ki and pid.kd can be changed directly at any time.",
        },
      ],
    },
    lage: {
      titel: "Orientation sensor (IMU)",
      einleitung:
        "The orientation sensor measures acceleration and turn rate. A filter calculates the tilt from these – and with Mahony or Madgwick the full orientation in space.",
      einstellungen: [
        {
          name: "Determining zero points",
          vorgabe: "3.0 s measurement time",
          bereich: "duration_s greater than 0",
          wirkung:
            "Measures the turn rates while stationary and subtracts them from then on. Without calibration the angles slowly drift.",
          hinweis:
            "The robot must stand still. If it moves, the measurement aborts with RobotFaultError. Measured on the device: without calibration the complementary filter and Mahony showed a deviation of 0.26° at rest, afterwards 0.06°.",
        },
        {
          name: "Choosing the filter",
          vorgabe: '"complementary"',
          bereich: '"complementary", "mahony", "madgwick"',
          wirkung:
            "Complementary filter: tilt only, the cheapest. Mahony and Madgwick: the full orientation as a quaternion – only then do imu.quaternion(), imu.orientation() and telemetry.send_orientation() return something.",
          hinweis:
            "Switching resets the tilt; imu.tilt() is None for one measurement step. Computing time per reading on the device: Mahony 230 µs, Madgwick 301 µs.",
        },
        {
          name: "Time constant (complementary filter)",
          vorgabe: "0.5 s",
          bereich: "greater than 0",
          wirkung:
            "Below it the turn rate counts, above it the acceleration. Small: follows quickly but is restless. Large: calm but sluggish.",
        },
        {
          name: "Weight of the turn rate (complementary filter)",
          vorgabe: "calculated from the time constant",
          bereich: "0 to 1",
          wirkung:
            "Sets the weight directly instead of via the time constant. 0 means acceleration only, 1 turn rate only – then the angle drifts away unchecked.",
          hinweis: "A new time constant resets a previously set gain.",
        },
        {
          name: "Correction kp and ki (Mahony)",
          vorgabe: "kp 2.0 · ki 0.0",
          bereich: "not negative",
          wirkung:
            "kp: how strongly the acceleration corrects the orientation; 1/kp is the time constant. ki: also estimates the zero point of the turn rate while driving, 0 switches this off.",
        },
        {
          name: "Step size beta (Madgwick)",
          vorgabe: "0.1",
          bereich: "not negative",
          wirkung:
            "Madgwick corrects every error with at most about 2 · beta rad/s, no matter how large it is – no time constant as with Mahony.",
        },
        {
          name: "Low-pass filter in the sensor",
          vorgabe: "44 Hz",
          bereich: "0 (off), 5, 10, 21, 44, 94 or 184",
          wirkung:
            "Smooths acceleration and turn rate already in the sensor. Lower: calmer, but delayed.",
          hinweis:
            "The new value is not written immediately but by the control core in the next cycle – writing from the program stopped the control core for up to 51 ms as measured.",
        },
        {
          name: "Calibration and reference angle by hand",
          vorgabe: "no zero points, scales 1, reference 0",
          bereich: "gyro_offset (rad/s), accel_offset (m/s²), accel_scale (factor), three values each",
          wirkung:
            "Sets zero points and scales without measuring, for example stored values from an earlier calibration. tilt_reference_rad is the angle that imu.tilt() outputs as 0.",
        },
      ],
      fest: [
        {
          name: "Read rate",
          wert: "every 4 ms (250 Hz)",
          bedeutung: "How often the control core reads the sensor and runs the filter.",
        },
      ],
    },
    odometrie: {
      titel: "Odometry",
      einleitung:
        "Odometry calculates from the wheel revolutions where the robot is and which way it faces.",
      einstellungen: [
        {
          name: "Setting the pose",
          vorgabe: "(0, 0, 0) at start-up",
          wirkung: "Defines where the robot is now and which way it points.",
          hinweis:
            "The pose drifts over time, above all the heading: wheel slip and the provisional values for wheel diameter and track width add up.",
        },
      ],
      fest: [
        {
          name: "Encoder",
          wert: "1440 counts per wheel revolution",
          bedeutung: "Resolution of the distance measurement: about 0.2 mm of travel per count.",
        },
      ],
    },
    telemetrie: {
      titel: "Telemetry",
      einleitung:
        "Measurements to the interface: as curves, for recording as CSV and as orientation in space.",
      einstellungen: [
        {
          name: "Sending the standard set regularly",
          vorgabe: "off; when switched on 0.1 s",
          bereich: "greater than 0",
          wirkung:
            "Sends battery, tilt, distance, wheel speeds and pose regularly. telemetry.stop() ends this.",
          hinweis:
            "More than 50 lines per second never go out; shorter periods only collect the values. Each line costs the robot just under 1 ms of computing time.",
        },
        {
          name: "Showing the orientation",
          vorgabe: "curves and 3D view",
          bereich: "plot and view each True or False",
          wirkung:
            "plot shows the four values as curves, view the robot in space. Both False: recording only.",
          hinweis:
            "Needs mode=\"mahony\" or \"madgwick\". With the complementary filter it returns False and sends nothing – the view then stays empty.",
        },
      ],
    },
    rueckkanal: {
      titel: "Controls from the interface",
      einleitung:
        "remote.number(), remote.switch(), remote.button() and remote.joystick() register controls in the interface and return their values.",
      einstellungen: [
        {
          name: "Timeout when the connection drops",
          vorgabe: "2.0 s",
          bereich: "greater than 0, or None to switch off",
          wirkung:
            "If the interface does not report for this long, all controls fall back to their default: a joystick to rest, sliders and switches to their initial value, pending button presses expire.",
          hinweis:
            "The timeout does not replace the stop. Whether the robot stops is decided by the program that passes on the joystick value. remote.timeout() without an argument returns the configured value.",
        },
      ],
      fest: [
        {
          name: "Polling the values",
          wert: "at most every 10 ms, in robot.sleep()",
          bedeutung:
            "New values only arrive in robot.sleep(). A loop with robot.sleep(0.05) therefore reads at most 20 times per second.",
        },
      ],
    },
    funk: {
      titel: "Bluetooth",
      einleitung:
        "Over Bluetooth the interface connects without a cable: sending programs, console, telemetry, controls.",
      einstellungen: [
        {
          name: "Switching the radio on",
          vorgabe: "off",
          bereich: "name with 1 to 29 characters",
          wirkung:
            "The robot advertises under this name and can be reached from the interface. From then on all output also goes over radio. ble.stop() switches it off.",
          hinweis:
            "Without ble.start() in the program, the robot cannot be reached over radio again after sending – every restart switches the radio off. So call it early in the program, right after robot.init(). The interface's stop button works over radio only if the program calls robot.sleep().",
        },
      ],
    },
    wlan: {
      titel: "Wi-Fi",
      einleitung: "For ROS 2 the robot needs a network – an existing one or its own.",
      einstellungen: [
        {
          name: "Connecting to a network",
          vorgabe: "15 s time limit",
          bereich: "WPA2-PSK only",
          wirkung: "Joins an existing network and returns the robot's own IP address.",
          hinweis:
            "A university network such as eduroam does not work – that is what the robot's own network is for.",
        },
        {
          name: "Opening a network of its own",
          vorgabe: "channel 6",
          bereich: "password at least 8 characters, or None for an open network",
          wirkung: "The robot sets up its own Wi-Fi; the computer connects to it.",
        },
      ],
    },
    ros: {
      titel: "ROS 2",
      einleitung: "Registers the robot in a ROS 2 network; needs running Wi-Fi.",
      einstellungen: [
        {
          name: "Registering",
          vorgabe: "20 Hz, port 9001, beacon port 9000",
          bereich:
            "rate_hz 1 to 50; name of lowercase letters, digits, underscore, not starting with a digit",
          wirkung:
            "The name becomes the namespace of the topics; rate_hz is the send rate of the measurements.",
        },
        {
          name: "Timeout for drive commands",
          vorgabe: "0.5 s",
          wirkung: "If no /cmd_vel arrives for this long, driving is set to zero.",
        },
        {
          name: "Timeout for the remote peer",
          vorgabe: "5.0 s",
          wirkung: "After this time without a message, a silent computer is forgotten.",
        },
      ],
    },
    sicherheit: {
      titel: "Safety – not adjustable",
      einleitung:
        "These values protect robot and people and cannot be changed from the program. It is worth knowing them, because they explain why a motor does not start or the robot suddenly stops.",
      einstellungen: [],
      fest: [
        {
          name: "Local stop",
          wert: "both buttons at the same time",
          bedeutung:
            "Stops all motors immediately and ends running jobs and your own controllers – in the control core, independently of the program, the browser and the radio link. Releasing alone restarts nothing. The way out of any malfunction.",
        },
        {
          name: "Motor voltage",
          wert: "at most 6.0 V",
          bedeutung:
            "The duty cycle is limited according to the battery voltage so that never more than 6 V reach the motor. battery.duty_limit() shows the current limit.",
        },
        {
          name: "Motor lock without battery measurement",
          wert: "below 4.0 V the measurement counts as invalid",
          bedeutung:
            "Without a valid measurement – no battery, only USB – the motors stay locked. An implausible value must not lead to full duty cycle as if it were a weak battery.",
        },
        {
          name: "Low battery",
          wert: "below 5.0 V",
          bedeutung: "From here on battery.is_low() reports a low battery.",
        },
        {
          name: "Base cycle of the control core",
          wert: "2 ms, control step every 4 ms",
          bedeutung:
            "The second processor core controls the motors, reads sensors and monitors the local stop at this rate – even if the program hangs.",
        },
        {
          name: "Ultrasound",
          wert: "2 cm to 4 m, every 60 ms",
          bedeutung:
            "Outside this range or without an echo, distance.read() returns None – never a made-up number. A measurement is valid for at most 250 ms.",
        },
      ],
    },
  },
};

export default en;
